/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Meta Data Sagas
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @version 1.25.0
 * @since 1.0.0
 */

import get from 'lodash/get';
import keys from 'lodash/keys';
import moment from 'moment';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take } from 'redux-saga/effects';

import { requestSaga } from '@/main/sagas/requestSaga';
import { API_HOSTNAME, LABEL_META, TABLE_META } from '@modules/app/Api';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import ICommonTranslation from '@modules/internationalization/types/common';
import { EExpensesAnalyzerField } from '@pages/PageExpensesAnalyzer/components/ExpensesAnalyzerTable/types/expensesAnalyzerTableTypes';
import { ESettingsAutoReportsField } from '@pages/PageSettings/components/SettingsAutoReports/components/SettingsAutoReportsTable/settingsAutoReportsTableTypes';

import { IOptionItemProps } from '../../common/Select/Select';
import { loadTableListAction, loadTableMetaError, loadTableMetaSuccessful } from '../actions/tableActions';
import { IOnFilterChangeParams } from '../components/Filters/Filter';
import { getDateFilterValue, IFilterDateParams } from '../components/Filters/FilterDate';
import { getRangeFilterValue, IFilterRangeParams } from '../components/Filters/FilterRange';
import { getTimeFilterValue } from '../components/Filters/FilterTime';
import { basketGetListStart } from '../components/footerModal/Basket/basketActions';
import { ILabel } from '../components/TableColumns/TableLabelBlock/TableLabelBlock';
import { TableWithLabels } from '../constants';
import { RESET_BASKET_SUCCESS, SET_RANGE_MONTH_FILTER_SUCCESS } from '../constants/actionConstants';
import { ITableReducer } from '../reducers/tableReducer';
import { makeSelectorIsFastBasketPage } from '../selectors/tableSelectors';
import { EColumnFilter } from '../types/columnTypes';
import { MapTableNameToApiPath, MapTableNameToFilterType } from '../types/tableMapTypes';
import { ITableDictionaryItem, TableNameType, TableWithBasket, TLoadTableMetaAction } from '../types/tableTypes';

export function* loadTableMetaSagas(action: TLoadTableMetaAction): SagaIterator {
    const effects = [];
    if (action.payload.waitFilterAndSort) {
        effects.push(take(SET_RANGE_MONTH_FILTER_SUCCESS));
    }
    if (action.payload.waitResetBasket) {
        effects.push(take(RESET_BASKET_SUCCESS));
    }
    yield all(effects);
    const tableName = action.payload.tableName;
    const queryParamsDirty = action.payload.queryParams ?? {};
    const queryParams = Object.fromEntries(
        Object.entries(queryParamsDirty).filter(([, value]) => value !== undefined && !!value.length),
    );
    let URL = API_HOSTNAME + MapTableNameToApiPath[tableName] + TABLE_META;
    if (queryParams) {
        const queryString = new URLSearchParams(queryParams).toString()
        URL += '?' + queryString;
    }
    const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());
    const isFastBasketPage = yield select(makeSelectorIsFastBasketPage());
    let labels = [];
    if (TableWithLabels.includes(tableName)) {
        try {
            const labelsUrl = API_HOSTNAME + MapTableNameToApiPath[tableName] + LABEL_META;
            const response = yield call(requestSaga, labelsUrl, 'GET');
            labels = get(response, 'elements');
        } catch (e) {
            console.log(e);
        }
    }
    try {
        const tableMeta = yield call(requestSaga, URL, 'GET');
        const exportExtensions = tableMeta.exportExtensions ?? [];
        const facets = tableMeta.facets ?? {};
        const fields = tableMeta.fields ?? [];
        const filter = tableMeta.filterAndSort?.filter ?? {};
        const buttons = tableMeta.buttons ?? [];
        const sort = tableMeta.filterAndSort?.sort ?? {};
        const parameters = tableMeta.parameters ?? null;
        let filterOptions = getOptions(facets, translation);

        if (labels.length) {
            filterOptions['labels'] = labels.map((label: ILabel) => {
                return {
                    value: label.id,
                    label: label.name,
                    color: 'color' + label.color,
                };
            });
        }
        if (tableName === TableNameType.bills) {
            filterOptions = { ...filterOptions, ...getBillsInfoFilters(get(tableMeta, 'billsInfo')) };
        }
        if (tableName === TableNameType.settingsAutoReports) {
            filterOptions = { ...filterOptions, ...getAutoReportsFilters(tableMeta) };
        }

        const metaFacets = keys(filterOptions) as ITableReducer[TableNameType]['metaFacets'];

        yield put(
            loadTableMetaSuccessful({
                buttons: buttons,
                exportExtensions,
                fields,
                filter: getFiltersValue(filter, tableName, translation, filterOptions),
                filterOptions,
                metaFacets,
                parameters,
                sort,
                tableName,
            }),
        );

        if (!action.payload.skipLoadList) {
            yield put(loadTableListAction({ force: true }));
        }

        if (TableWithBasket.includes(tableName) && !isFastBasketPage) {
            yield put(basketGetListStart({ start: true }));
        }
    } catch (error: any) {
        yield put(loadTableMetaError({ tableName: tableName, error }));
    }
}

const EAssistantCampaignsCount = {
    assistantCampaignsCount: 'assistantCampaignsCount',
    exist: 1,
    noExist: 0,
};
const ECoordinatorCampaignsCount = {
    coordinatorCampaignsCount: 'coordinatorCampaignsCount',
    exist: 1,
    noExist: 0,
};

// для фильтрации строк без значения
export const TABLE_NO_VALUE_OPTION = 'no-value';

/**
 * The method convert string filter options to IOptionItemProps filter options
 * @param {[Key in string]: string[]} facets - map of table filter options
 * @param {ICommonTranslation} translation - current locale
 * @return {[Key in string]: IOptionItemProps[]}
 */
export function getOptions(
    facets: { [Key in string]: string[] },
    translation: ICommonTranslation,
): { [Key in string]: IOptionItemProps[] } {
    const result: { [Key in string]: IOptionItemProps[] } = {};
    for (const key in facets) {
        result[key] = facets[key].map((item: string): IOptionItemProps => {
            return {
                value: item,
                label: item === TABLE_NO_VALUE_OPTION ? translation.commonTable.noValueFilter : item,
            };
        });
    }

    result[EAssistantCampaignsCount.assistantCampaignsCount] = [
        { value: 'noExist', label: translation.select.noExist },
        { value: 'exist', label: translation.select.exist },
    ];

    result[ECoordinatorCampaignsCount.coordinatorCampaignsCount] = [
        { value: 'noExist', label: translation.select.noExist },
        { value: 'exist', label: translation.select.exist },
    ];

    return result;
}

/**
 * The method convert string filter options to IOptionItemProps filter options
 * @param {[Key in string]: string[]} filters - map of table filter options
 * @param {TableNameType} tableName
 * @param {ICommonTranslation} translation - current locale
 * @param {[key in string]: IOptionItemProps[]} filterOptions - additional options for filter
 * @return {[Key in string]: IOptionItemProps[]}
 */
function getFiltersValue(
    filters: { [Key in string]: any },
    tableName: TableNameType,
    translation: ICommonTranslation,
    filterOptions?: { [key in string]: IOptionItemProps[] },
): { [Key in string]: IOnFilterChangeParams } {
    const result: { [Key in string]: IOnFilterChangeParams } = {};
    for (const key in filters) {
        if (filters[key] && (filters[key].value || filters[key].value === EAssistantCampaignsCount.noExist)) {
            const filter = MapTableNameToFilterType[tableName](key);
            switch (filter) {
                case EColumnFilter.filterMultiSelect:
                    result[key] = filters[key].value.map((item: string): IOptionItemProps => {
                        return { value: item, label: item };
                    });
                    break;
                case EColumnFilter.filterSelect:
                    if (
                        key === EAssistantCampaignsCount.assistantCampaignsCount ||
                        key === ECoordinatorCampaignsCount.coordinatorCampaignsCount
                    ) {
                        const value =
                            filters[key].value === EAssistantCampaignsCount.exist ||
                            filters[key].value === ECoordinatorCampaignsCount.exist
                                ? translation.select.exist
                                : translation.select.noExist;
                        result[key] = { value: filters[key].value, label: value };
                    } else {
                        const value = filters[key].value;
                        let label = value;

                        if (filterOptions && Array.isArray(filterOptions[key]) && filterOptions[key].length) {
                            const filterLabel = filterOptions[key].find((option) => {
                                return option.value === value;
                            });

                            if (filterLabel) {
                                label = filterLabel.label;
                            }
                        }

                        result[key] = { value, label };
                    }
                    break;
                case EColumnFilter.filterRange:
                    result[key] = filters[key].value;
                    (result[key] as IFilterRangeParams).filterValue = getRangeFilterValue(
                        get(result[key], 'valueFrom', ''),
                        get(result[key], 'valueTo', ''),
                    );
                    break;
                case EColumnFilter.filterTime:
                case EColumnFilter.filterDate:
                    result[key] = {
                        endDate: undefined,
                        filterValue: '',
                        startDate: undefined,
                    };
                    const valueFrom = get(filters[key], 'value.valueFrom', '');
                    const valueTo = get(filters[key], 'value.valueTo', '');
                    const filterValue =
                        filter === EColumnFilter.filterTime
                            ? getTimeFilterValue(moment(valueFrom), moment(valueTo))
                            : getDateFilterValue(moment(valueFrom), moment(valueTo));
                    (result[key] as IFilterDateParams).startDate = valueFrom;
                    (result[key] as IFilterDateParams).endDate = valueTo;
                    (result[key] as IFilterDateParams).filterValue = filterValue;
                    break;
                default:
                    result[key] = filters[key].value;
                    break;
            }
        }
        // Month range filter
        if (filters[key] && key === EExpensesAnalyzerField.MONTH_RANGE && filters[key].valueFrom) {
            result[key] = {
                valueTo: filters[key].valueTo,
                filterValue: '',
                valueFrom: filters[key].valueFrom,
            };
        }
        // Labels filter
        if (filters[key] && filters[key].include && filterOptions && filterOptions.labels) {
            result[key] = filters[key].include.map((id: string): IOptionItemProps => {
                const label = filterOptions.labels.find(
                    (label: IOptionItemProps) => label.value.toString() === id.toString(),
                );
                if (label) {
                    return { value: id, label: label.label };
                }
                return { value: id, label: id.toString() };
            });
        }
    }
    return result;
}

interface IAutoReportsInfo {
    orderTypes: ITableDictionaryItem[];
    periodTypes: ITableDictionaryItem[];
    reports: ITableDictionaryItem[];
    reportFormats: ITableDictionaryItem[];
}

export function getAutoReportsFilters(params: IAutoReportsInfo): { [key in string]: IOptionItemProps[] } {
    return {
        [ESettingsAutoReportsField.ORDER_ATTR_TYPE]: params.orderTypes.map((item) => ({
            label: item.def,
            value: item.id,
        })),
        [ESettingsAutoReportsField.PERIOD_TYPE]: params.periodTypes.map((item) => ({
            label: item.def,
            value: item.id,
        })),
        [ESettingsAutoReportsField.REPORT_NAME]: params.reports.map((item) => ({ label: item.def, value: item.id })),
        [ESettingsAutoReportsField.REPORT_FORMAT]: params.reportFormats.map((item) => ({
            label: item.def,
            value: item.def,
        })),
    };
}

interface IPayStatuses {
    id: string;
    name: string;
}

interface IBillsInfo {
    payStatuses: IPayStatuses[];
}

function getBillsInfoFilters(billsInfo: IBillsInfo): { [key in string]: IOptionItemProps[] } {
    return {
        payStatus: billsInfo.payStatuses.map((item: IPayStatuses) => ({ value: item.id, label: item.name })),
    };
}
